import fetchClient, { ApiResponse } from '@/api/api-client'
import { UpdateUserPayload } from '@/api/auth/mutations/useUpdateUser'
import { CognitoUser, LoggedUser } from '@/api/auth/types/LoggedUser'
import { PkgLookupItem } from '@/api/pkg-lookup/types'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'

import type { AddCreditPayload } from './types/AddCreditPayload'
import type { AgentNote } from './types/AgentNote'
import { User, UserWithCognito } from './types/User'
import type { UserSearchResult } from './types/UserSearchResult'

export type SearchUserForNewPkgParams = {
  trackingNumberInTurkey?: string
  phoneNumber?: string
  primarkOrderNumber?: string
  userId?: string
  warehouse?: PkgWarehouse
}

export type SearchUserForNewPkgResult = {
  user: User
  pkgLookups: PkgLookupItem[]
}

export async function searchUsersInDb(searchValue: string, limit = 50) {
  const res = await fetchClient.post<ApiResponse<UserSearchResult[]>>('/users/search-db', {
    searchValue,
    limit,
  })
  return res.data.data
}

export async function searchUserByPhoneOrTracking(searchFields: { trackingNumberInTurkey?: string; phoneNumber?: string }) {
  const res = await fetchClient.post<ApiResponse<CognitoUser>>('/users/search', searchFields)
  return res.data.data
}

export async function searchUserForNewPkg(searchFields: SearchUserForNewPkgParams) {
  const res = await fetchClient.post<ApiResponse<SearchUserForNewPkgResult>>('/users/search-for-new-pkg', searchFields)
  return res.data.data
}

export async function getCurrentUser() {
  const res = await fetchClient.get<ApiResponse<LoggedUser>>(`/users/me`)
  return res.data.data
}

export async function completeOnboarding() {
  const res = await fetchClient.patch<ApiResponse<LoggedUser>>(`/users/me/onboarding`)
  return res.data.data
}

export async function updateUser(id: string, params: UpdateUserPayload) {
  const res = await fetchClient.put(`/users/${id}`, params)
  return res.data.data
}

export async function deleteUser(username: string) {
  const res = await fetchClient.delete(`/users/${username}`)
  return res.data.data
}

export async function confirmUser(username: string) {
  const res = await fetchClient.post(`/auth/admin-confirm-user`, { username })
  return res.data.data
}

export async function currentAuthenticatedUser() {
  const token = JSON.parse(localStorage.getItem('cognito-tokens') || '{}')
  return Object.keys(token).length > 0 && (await getCurrentUser())
}

export async function getUserById(userId: string) {
  const res = await fetchClient.get<ApiResponse<UserWithCognito>>(`/users/${userId}`)
  return res.data.data
}

export async function addCredit(payload: AddCreditPayload): Promise<User> {
  const res = await fetchClient.post<ApiResponse<User>>('/users/credit/add', payload)
  return res.data.data
}

export async function addUserNote(userId: string, body: string): Promise<AgentNote[]> {
  const res = await fetchClient.post<ApiResponse<AgentNote[]>>(`/users/${userId}/notes`, { body })
  return res.data.data
}

export async function removeUserNote(userId: string, noteId: string): Promise<AgentNote[]> {
  const res = await fetchClient.delete<ApiResponse<AgentNote[]>>(`/users/${userId}/notes/${noteId}`)
  return res.data.data
}
